import useStyles from './styles';

import React from 'react';

import { observer } from 'mobx-react';

import moment from 'moment';

import { Card, CardActionArea, Typography } from '@material-ui/core';

import CKEditorViewer from 'bz-core-controls/ckeditor/viewer';
import Fallback from 'bz-core-controls/controls/lazy/controls/fallback';

import withDisplayName from 'bz-utilities/hoc/display-name';
import useComputed from 'bz-utilities/hooks/computed';
import createContext from 'bz-utilities/hooks/context-v2';

import { useProviderStore } from 'connect/web/src/stores/provider-store';

import { PackageType } from 'tm3-core/api.dtos';
import type {
  CustomerPackagesResponse,
  CustomerSubscriptionResponse,
  PackageResponse
} from 'tm3-core/api.dtos';

import CardMedia from '../../../card-media';
import Status from './controls/status';
import SubscriptionDetails from './controls/subscription-details';

const Actions = React.lazy(() => import('./controls/actions'));
const PackageBundles = React.lazy(() => import('./controls/bundles'));
const PackageCredits = React.lazy(() => import('./controls/credits'));

export const [PkgCtx, usePkgCtx] = createContext<
  CustomerPackagesResponse | CustomerSubscriptionResponse
>('PkgCtx');

interface IProps {
  showActions?: boolean;
  isDashboard?: boolean;
  purchased?: boolean;

  onSelectPackage?: (packageId: string, isSubscription: boolean) => void;
}

const Package = observer(
  withDisplayName<IProps>((props) => {
    const providerStore = useProviderStore();

    const classes = useStyles();

    const { isDashboard, showActions, onSelectPackage } = props;

    const pkg = usePkgCtx();
    const { description, longDescription, imageName } = pkg as unknown as PackageResponse;

    const cost = useComputed((): string => {
      const { totalPrice } = pkg;
      const { currency } = providerStore;

      return currency + totalPrice.toFixed(2);
    }, [pkg, providerStore]);

    const expiryDate = useComputed((): string | null => {
      const { expiryDate } = pkg;

      if (!expiryDate) {
        return null;
      }

      return 'Valid Until: ' + moment(expiryDate).format('DD/MM/YYYY');
    }, [pkg]);

    const browserClass = useComputed((): string => {
      const { safari, msExplorer } = classes;
      const version = window.document.documentMode || 0;

      if (version > 0) {
        return msExplorer;
      }
      return safari;
    }, [classes]);

    const cls = useComputed((): string => {
      return [classes.fullHeight, browserClass].toClassNames();
    }, [classes, browserClass]);

    const onSelect = React.useCallback(
      (isSubscription: boolean): void => {
        const { onlineGuid } = pkg;
        onSelectPackage?.(onlineGuid, isSubscription);
      },
      [pkg, onSelectPackage]
    );

    let details;
    switch (pkg.packageType) {
      case PackageType.ITEM:
        details = <PackageBundles pkg={pkg} />;
        break;

      case PackageType.CREDIT:
        details = <PackageCredits purchased={props.purchased} />;
        break;
    }

    let expiryContainer;
    if (!!expiryDate) {
      expiryContainer = <div className={classes.expiryContainer}>{expiryDate}</div>;
    }

    let actions;
    if (showActions) {
      actions = <Actions pkg={pkg} onSelectPackage={onSelect} />;
    }

    let content = (
      <div className={classes.cardContainer}>
        <Status
          status={
            ((pkg as CustomerSubscriptionResponse).subscriptionStatus as unknown as string) ||
            pkg.status
          }
          expiryDate={pkg.expiryDate}
          isSubscription={pkg.isSubscription}
        />
        <CardMedia imageUrl={imageName} className={classes.imageContainer} />
        <div className={classes.packageDetails}>
          <div className={classes.top}>
            <div className={classes.header}>
              <Typography color="primary" component="h3">
                {description}
              </Typography>
            </div>
            <Typography color="primary" component="h3">
              {cost}
            </Typography>
          </div>
          <CKEditorViewer className={classes.longDescription} value={longDescription || ''} />
          <hr />
          {details}
        </div>
        {pkg.isSubscription && <SubscriptionDetails pkg={pkg} />}
        {expiryContainer}
        {actions}
      </div>
    );

    if (isDashboard) {
      content = (
        <CardActionArea className={cls} onClick={onSelect}>
          {content}
        </CardActionArea>
      );
    }

    return (
      <div className={classes.packageContainer}>
        <Card className={cls}>
          <React.Suspense fallback={<Fallback />}>{content}</React.Suspense>
        </Card>
      </div>
    );
  }, 'Package')
);

export default Package;
